import { useMemo, useState } from "react";

import { GetManualLabsRequestFilteringCriteria } from "@/Common/Api/Labs/Dto/Responses/GetManualLabsRequestFilteringCriteria";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { Guid } from "@/Common/Models/Guid";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxLabel } from "@/DesignSystem";

import styles from "./ManualLabsInboxSettingsContainer.module.scss";

interface IManualLabsInboxSettingsContainerProps {
    filterSettings: ManualLabsInboxFilteringConfiguration;
    onOkClick: (manualLabsGridFilterSettings: ManualLabsInboxFilteringConfiguration) => void;
    onCancelClick: () => void;
}

class ManualLabsInboxFilteringConfiguration extends GetManualLabsRequestFilteringCriteria {
    public shouldSaveSettings: boolean;
    public pageSizeOptions = [50, 100, 150];
    public showDeactivatedClinicians = false;
    public selectedSites: string[] = [];

    constructor(fields?: Partial<ManualLabsInboxFilteringConfiguration>) {
        super();
        Object.assign(this, fields);
    }
}

function ManualLabsInboxSettingsContainer(props: Readonly<IManualLabsInboxSettingsContainerProps>): JSX.Element {
    const allSites = useMemo(() => cacheStore.GetSites(null), []);

    const [filterSettingsState, setFilterSettingsState] = useState<ManualLabsInboxFilteringConfiguration>(props.filterSettings);

    function filterText(str: string, fullList: Immutable.List<EmployeeUIModel>): Immutable.List<EmployeeUIModel> {
        return str !== ""
            ? SearchMenuHelper.FilterCliniciansByName(str, fullList)
            : fullList;
    }

    const allClinicians = useMemo(() => Immutable.List(SearchMenuHelper.GetCliniciansList(null)), []);

    const siteFilteredClinicians = useMemo(
        () => {
            if (!filterSettingsState.selectedSites.length)
                return allClinicians;

            return Immutable.List(SearchMenuHelper.FilterCliniciansMembersOfSites(
                allClinicians,
                filterSettingsState.selectedSites
            ));
        },
        [allClinicians, filterSettingsState.selectedSites]
    );

    const siteAndTextFilteredClinicians = useMemo(
        () => filterText(filterSettingsState.searchText, siteFilteredClinicians),
        [filterSettingsState.searchText, siteFilteredClinicians]
    );

    const areAllSitesSelected: boolean = allSites.all(site => filterSettingsState.selectedSites.includes(site.Id));

    const cliniciansToRender = useMemo(
        () => siteAndTextFilteredClinicians.filterImmutable(clinician => clinician.ContactFacetId != sessionStore.getCurrentEmployee().ContactFacetId),
        [siteAndTextFilteredClinicians]
    );
    const cliniciansToRenderself = useMemo(
        () => siteAndTextFilteredClinicians.filterImmutable(clinician => clinician.ContactFacetId == sessionStore.getCurrentEmployee().ContactFacetId).first(),
        [siteAndTextFilteredClinicians]
    );

    const isSelectAllCliniciansCheckboxDisabled = !siteAndTextFilteredClinicians.count();

    const areAllSelectableCliniciansSelected: boolean = siteFilteredClinicians.count()
        && siteFilteredClinicians.toArray().all(clinician => filterSettingsState.selectedCliniciansContactFacetIds.contains(clinician.ContactFacetId));

    function handleOnCancelClick(): void {
        props.onCancelClick();
    }

    function handleOnClick(): void {
        props.onOkClick(filterSettingsState);
    }

    function handleClinicianCheckboxChange(clinician: EmployeeUIModel, checked: boolean): void {
        setFilterSettingsState((prevState: ManualLabsInboxFilteringConfiguration): ManualLabsInboxFilteringConfiguration => {
            const selectedClinicians: Guid[] = checked
                ? [...prevState.selectedCliniciansContactFacetIds, clinician.ContactFacetId]
                : prevState.selectedCliniciansContactFacetIds.filter(facetId => facetId !== clinician.ContactFacetId);
            return { ...prevState, selectedCliniciansContactFacetIds: selectedClinicians };
        });
    }

    function handleSelectAllCliniciansCheckboxChange(checked: boolean): void {
        setFilterSettingsState((prevState: ManualLabsInboxFilteringConfiguration): ManualLabsInboxFilteringConfiguration => ({
            ...prevState,
            selectedCliniciansContactFacetIds: checked ? [...siteFilteredClinicians.toArray().map(p => p.ContactFacetId)] : []
        }));
    }

    function handleSiteCheckboxChange(siteId: string, checked: boolean): void {
        setFilterSettingsState((prevState: ManualLabsInboxFilteringConfiguration): ManualLabsInboxFilteringConfiguration => {
            const selectedSites: string[] = checked
                ? [...prevState.selectedSites, siteId]
                : prevState.selectedSites.filter(id => id !== siteId);
            return { ...prevState, selectedSites: selectedSites };
        });
    }

    function handleSelectAllSitesChange(checked: boolean): void {
        setFilterSettingsState((prevState: ManualLabsInboxFilteringConfiguration): ManualLabsInboxFilteringConfiguration => ({
            ...prevState,
            selectedSites: checked ? allSites.map(site => site.Id) : []
        }));
    }

    return (
        <div className={styles.manualLabsInboxSettingsContainer}>

            <div className={styles.title}>
                <label style={{ display: "inline" }}>{cacheStore.GetString("MessageDateLabel").toUpperCase()}</label>
            </div>

            <div className={styles.title}>
                <CheckboxLabel
                    id="sites_check"
                    label={cacheStore.GetString("SitesLabel").toUpperCase()}
                    checked={areAllSitesSelected}
                    onChange={handleSelectAllSitesChange}
                />
            </div>

            <div className={styles.title}>
                <CheckboxLabel
                    id="clinicians_check"
                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                    checked={areAllSelectableCliniciansSelected}
                    onChange={handleSelectAllCliniciansCheckboxChange}
                    disabled={isSelectAllCliniciansCheckboxDisabled}
                />
            </div>

            <div className={styles.title}>
                <span>{cacheStore.GetString("SettingsLabel").toUpperCase()}</span>
            </div>

            <div className={styles.controlsContainer}>
                <MultiDateSelector
                    id="doc_search_end_date_selector"
                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                    from={filterSettingsState.from}
                    to={filterSettingsState.to}
                    fromChange={date => setFilterSettingsState({ ...filterSettingsState, from: date })}
                    toChange={date => setFilterSettingsState({ ...filterSettingsState, to: date })}
                />
            </div>

            <div className={styles.controlsContainer}>
                {allSites?.map(site => (
                    <CheckboxLabel
                        key={site.Id}
                        id={`site_${site.Id}`}
                        label={site.Name}
                        checked={filterSettingsState.selectedSites.includes(site.Id)}
                        onChange={checked => handleSiteCheckboxChange(site.Id, checked)}
                    />
                ))}
            </div>

            <div className={styles.controlsContainer}>
                <input
                    className={`form-control ${styles.textSearch}`}
                    type="text"
                    value={filterSettingsState.searchText}
                    onChange={event => setFilterSettingsState({ ...filterSettingsState, searchText: event.target.value })}
                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                />

                {cliniciansToRenderself && (
                    <CheckboxLabel
                        label={cliniciansToRenderself.Name}
                        checked={filterSettingsState.selectedCliniciansContactFacetIds?.includes(cliniciansToRenderself.ContactFacetId)}
                        onChange={checked => handleClinicianCheckboxChange(cliniciansToRenderself, checked)}
                    />
                )}

                {cliniciansToRender.map(clinician => (
                    <CheckboxLabel
                        key={clinician.ContactFacetId}
                        label={clinician.Name}
                        checked={filterSettingsState.selectedCliniciansContactFacetIds?.includes(clinician.ContactFacetId)}
                        onChange={checked => handleClinicianCheckboxChange(clinician, checked)}
                    />
                ))}
            </div>

            <div className={styles.controlsContainer}>
                <CheckboxLabel
                    id="ShowAbnormalsOnly"
                    checked={filterSettingsState.showAbnormalResultsOnly}
                    label={cacheStore.GetString("AbnormalOnly")}
                    onChange={checked => setFilterSettingsState({ ...filterSettingsState, showAbnormalResultsOnly: checked })}
                />
                <CheckboxLabel
                    id="HideLabResultsFiledByColleague"
                    checked={filterSettingsState.hideLabResultsFiledByColleague}
                    label={cacheStore.GetString("HideResultsFiledByColleagueLabel")}
                    onChange={checked => setFilterSettingsState({ ...filterSettingsState, hideLabResultsFiledByColleague: checked })}
                />
            </div>

            <div className={styles.footer}>
                <SearchSettingsFooter
                    onClose={handleOnCancelClick}
                    onOk={handleOnClick}
                    onColumnOrderingButtonClick={null}
                    onResultsPerPageChange={value => setFilterSettingsState({ ...filterSettingsState, pageNumber: 1, pageSize: value })}
                    selectedPageSizeOption={filterSettingsState.pageSize}
                    pageSizeOptions={filterSettingsState.pageSizeOptions}
                    isSaveSettingsChecked={filterSettingsState.shouldSaveSettings}
                    onSaveSettingsCheckboxChange={checked => setFilterSettingsState({ ...filterSettingsState, shouldSaveSettings: checked })}
                />
            </div>
        </div>
    );
}

export { ManualLabsInboxFilteringConfiguration, ManualLabsInboxSettingsContainer };
